<script>
import { AbcHeading } from "@ordbogen-npm/abc-dsk";
import { storeToRefs } from "pinia";
import { computed, defineComponent, toRefs } from "vue";
import { useI18n } from "vue-i18n";

import TgButton from "@/abc-dsk/components/Button/TgButton.vue";
import { formatName } from "@/common/utils";
import LottieAnimation from "@/components/LottieAnimation.vue";
import ClassQuizBaseLayout from "@/components/vocabularyTrainer/ClassQuizBaseLayout.vue";
import QuizJumbotron from "@/components/vocabularyTrainer/QuizJumbotron.vue";
import { useAuthStore } from "@/stores";

import { useClassQuizComponent } from "./use-class-quiz-component";

const { commonProps, commonEmits } = useClassQuizComponent();

export default defineComponent({
  components: {
    ClassQuizBaseLayout,
    LottieAnimation,
    QuizJumbotron,
    TgButton,
    AbcHeading
  },
  props: commonProps,
  emits: commonEmits,
  setup(props, { emit }) {
    // Import globals
    const authStore = useAuthStore();
    const { t } = useI18n();

    const { disableContinue, classQuiz } = toRefs(props);

    const { userIsTeacher } = storeToRefs(authStore);
    const topPlayers = computed(() => classQuiz.value.topPlayers);

    const onContinue = () => emit("continue");

    return {
      t,

      topPlayers,
      formatName,

      disableContinue,
      userIsTeacher,
      onContinue
    };
  }
});
</script>

<template>
  <ClassQuizBaseLayout>
    <template #jumbotron>
      <QuizJumbotron
        color="secondary"
        class="jumbotron"
        :class="{ 'jumbotron--slim': !userIsTeacher }"
      >
        <div class="container">
          <AbcHeading v-if="userIsTeacher" tag="h1" size="xl" class="text-center">
            {{ t("vocab.classQuiz.ranking.header") }}
          </AbcHeading>
        </div>
      </QuizJumbotron>
    </template>

    <div v-if="userIsTeacher" class="container ranking-container">
      <table class="ranking-table">
        <tr v-for="(player, index) in topPlayers" :key="index">
          <td class="ranking-table__cell" :class="{ 'ranking-table__cell--lead': index === 0 }">
            {{
              t("vocab.classQuiz.ranking.playerRanking", {
                rank: index + 1,
                name: formatName(player.name)
              })
            }}
          </td>
          <td
            class="ranking-table__cell text-right"
            :class="{ 'ranking-table__cell--lead': index === 0 }"
          >
            {{
              t("vocab.classQuiz.ranking.playerCorrectAnswers", { count: player.correctAnswers })
            }}
          </td>
        </tr>
      </table>
    </div>
    <div v-else class="container animation-container">
      <p class="sr-only">{{ t("vocab.classQuiz.ranking.waiting") }}</p>
      <LottieAnimation path="waiting" :loop="true" class="waiting-animation" />
    </div>

    <template v-if="userIsTeacher" #footer>
      <div class="container text-center">
        <TgButton size="sm" size-md="md" :disabled="disableContinue" @click="onContinue()">
          {{ t("vocab.classQuiz.ranking.nextQuestion") }}
        </TgButton>
      </div>
    </template>
  </ClassQuizBaseLayout>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.jumbotron {
  margin-bottom: 0;
  padding: 50px 0;

  #media-breakpoint-up(md, {
    padding: 100px 0;
  });
}

.ranking-container {
  display: flex;
  justify-content: center;
  margin-top: 32px;
}

.ranking-table {
  width: 100%;
  max-width: 540px;
  table-layout: fixed;

  &__cell {
    width: 100%;
    padding: 24px;
    overflow: hidden;
    border-bottom: 1px solid rgb(@theme-colors[@grey][50]);
    font-size: 1.5rem;
    font-weight: 500;
    line-height: 1.75rem;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:last-of-type {
      max-width: 50%;
    }

    &--lead {
      border-bottom: none;
      background: rgb(@theme-colors[@primary][base]);
      color: @colors[@white];

      &:first-of-type {
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
      }

      &:last-of-type {
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
      }
    }
  }
}

.animation-container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 32px;
}

.waiting-animation {
  max-width: 400px;
}
</style>
